/**
 * Routing Logs Database Hook
 * 
 * Provides database operations for the routing_logs table.
 * Abstracts Supabase queries and transforms data to/from AllocationLog format.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { AllocationLog, AllocationBreakdown, EvaluatedAgent, AgentPickAttempt, PoolMeta } from '@/contexts/AllocationLogContext';

// --- Database Row Type (internal) ---

interface RoutingLogRow {
  id: string;
  lead_id: string;
  lead_type_id: string;
  lead_type_name: string;
  lead_state: string;
  result: string;
  rejection_reason: string | null;
  rejection_code: string | null;
  charged_amount: number | null;
  priority_score: number | null;
  allocation_breakdown: AllocationBreakdown | null;
  selected_agent_id: string | null;
  selected_agent_name: string | null;
  evaluated_agents: EvaluatedAgent[] | null;
  pick_attempts: AgentPickAttempt[] | null;
  total_agents_evaluated: number | null;
  eligible_count: number | null;
  ineligible_count: number | null;
  lead_status_before: string | null;
  lead_status_after: string | null;
  retry_count: number | null;
  pool_meta: PoolMeta | null;
  created_at: string;
}

// --- Transform Functions ---

function rowToLog(row: RoutingLogRow): AllocationLog {
  return {
    id: row.id,
    leadId: row.lead_id,
    leadTypeId: row.lead_type_id,
    leadTypeName: row.lead_type_name,
    leadState: row.lead_state,
    result: row.result as 'success' | 'rejected' | 'unsold',
    rejectionReason: row.rejection_reason ?? undefined,
    rejectionCode: row.rejection_code ?? undefined,
    chargedAmount: row.charged_amount ?? undefined,
    priorityScore: row.priority_score ?? undefined,
    allocationBreakdown: row.allocation_breakdown ?? undefined,
    selectedAgentId: row.selected_agent_id ?? undefined,
    selectedAgentName: row.selected_agent_name ?? undefined,
    evaluatedAgents: row.evaluated_agents ?? undefined,
    pickAttempts: row.pick_attempts ?? undefined,
    totalAgentsEvaluated: row.total_agents_evaluated ?? undefined,
    eligibleCount: row.eligible_count ?? undefined,
    ineligibleCount: row.ineligible_count ?? undefined,
    leadStatusBefore: row.lead_status_before ?? undefined,
    leadStatusAfter: row.lead_status_after ?? undefined,
    retryCount: row.retry_count ?? undefined,
    poolMeta: row.pool_meta ?? undefined,
    timestamp: row.created_at,
  };
}

export interface CreateRoutingLogInput {
  leadId: string;
  leadTypeId: string;
  leadTypeName: string;
  leadState: string;
  result: 'success' | 'rejected' | 'unsold';
  rejectionReason?: string;
  rejectionCode?: string;
  chargedAmount?: number;
  priorityScore?: number;
  allocationBreakdown?: AllocationBreakdown;
  selectedAgentId?: string;
  selectedAgentName?: string;
  evaluatedAgents?: EvaluatedAgent[];
  pickAttempts?: AgentPickAttempt[];
  totalAgentsEvaluated?: number;
  eligibleCount?: number;
  ineligibleCount?: number;
  leadStatusBefore?: string;
  leadStatusAfter?: string;
  retryCount?: number;
  poolMeta?: PoolMeta;
}

// --- Query Keys ---

export const routingLogsQueryKeys = {
  all: ['routing-logs'] as const,
  byResult: (result: 'success' | 'rejected' | 'unsold') => ['routing-logs', 'result', result] as const,
  byLeadId: (leadId: string) => ['routing-logs', 'lead', leadId] as const,
};

// --- Hooks ---

export function useRoutingLogs() {
  return useQuery({
    queryKey: routingLogsQueryKeys.all,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('routing_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      
      if (error) throw error;
      return (data as unknown as RoutingLogRow[]).map(rowToLog);
    },
  });
}

export function useRoutingLogsByResult(result: 'success' | 'rejected' | 'unsold') {
  return useQuery({
    queryKey: routingLogsQueryKeys.byResult(result),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('routing_logs')
        .select('*')
        .eq('result', result)
        .order('created_at', { ascending: false })
        .limit(500);
      
      if (error) throw error;
      return (data as unknown as RoutingLogRow[]).map(rowToLog);
    },
  });
}

export function useRoutingLogsByLeadId(leadId: string) {
  return useQuery({
    queryKey: routingLogsQueryKeys.byLeadId(leadId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('routing_logs')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data as unknown as RoutingLogRow[]).map(rowToLog);
    },
    enabled: !!leadId,
  });
}

// --- Mutations ---

export function useCreateRoutingLog() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: CreateRoutingLogInput) => {
      const logId = `log-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Build the row object with proper types for JSONB fields
      const insertRow = {
        id: logId,
        lead_id: input.leadId,
        lead_type_id: input.leadTypeId,
        lead_type_name: input.leadTypeName,
        lead_state: input.leadState,
        result: input.result,
        rejection_reason: input.rejectionReason ?? null,
        rejection_code: input.rejectionCode ?? null,
        charged_amount: input.chargedAmount ?? null,
        priority_score: input.priorityScore ?? null,
        allocation_breakdown: input.allocationBreakdown as unknown as null,
        selected_agent_id: input.selectedAgentId ?? null,
        selected_agent_name: input.selectedAgentName ?? null,
        evaluated_agents: input.evaluatedAgents as unknown as null,
        pick_attempts: input.pickAttempts as unknown as null,
        total_agents_evaluated: input.totalAgentsEvaluated ?? null,
        eligible_count: input.eligibleCount ?? null,
        ineligible_count: input.ineligibleCount ?? null,
        lead_status_before: input.leadStatusBefore ?? null,
        lead_status_after: input.leadStatusAfter ?? null,
        retry_count: input.retryCount ?? null,
        pool_meta: input.poolMeta as unknown as null,
      };
      
      const { data, error } = await supabase
        .from('routing_logs')
        .insert(insertRow as any)
        .select()
        .single();
      
      if (error) throw error;
      return rowToLog(data as unknown as RoutingLogRow);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: routingLogsQueryKeys.all });
    },
  });
}

export function useDeleteAllRoutingLogs() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('routing_logs')
        .delete()
        .neq('id', ''); // Delete all
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: routingLogsQueryKeys.all });
    },
  });
}
